/**
  * Uses canvas.measureText to compute and return
  * the width of the given text of given font in pixels.
  *
  * @param {String} text The text to be rendered.
  * @param {String} font The css font descriptor that
  * text is to be rendered with (e.g. "bold 14px verdana").
  *
  * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
  */
const canvas = document.createElement('canvas');
export function getTextWidth(text: string, font: string) {
  const context = canvas.getContext('2d');
  if (!context) return 0;
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
}

export const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const abbreviateNumber = (value: number | string): string => {
  const num = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(num) || Math.abs(num) < 1000) return String(value);

  const valueInThousands = num / 1000;
  const isRoundThousands = valueInThousands % 1 === 0;

  return isRoundThousands ? `${valueInThousands}k` : `${valueInThousands.toFixed(1)}k`;
};

export const hexHash7 = (str: string) => {
  /* eslint-disable no-bitwise */
  let hash = 2166136261; // FNV-1a 32-bit offset basis
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  hash >>>= 0; // Ensure unsigned
  return hash.toString(16).padStart(7, '0').slice(0, 7); // Truncate to 7 hex digits
};

export const hexHash7FromObject = (obj: Record<string, any>) => {
  const str = JSON.stringify(obj, Object.keys(obj).sort());
  return hexHash7(str);
};

export const sha256 = async (message: string): Promise<string> => {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * This is more efficient than using Lodash's isEqual for comparing two arrays of strings,
 * as it returns false as soon as a mismatch is found, without needing to check the entire array
 * or compute hashes. It also assumes that the order of strings in the arrays matters.
 */
export const stringArrayEqual = (
  a: string[],
  b: string[],
) => a.length === b.length && a.every((id, i) => id === b[i]);

export const stringArrayMemoizeOptions = {
  memoizeOptions: { resultEqualityCheck: stringArrayEqual },
};

const capDecimals = (num: number, maxPlaces: number) => {
  const factor = 10 ** maxPlaces;
  return Math.round(num * factor) / factor;
};

type PercentageValue = string | number | null | undefined;
export const isPercentage = (value: PercentageValue): boolean => {
  if (value === null || value === undefined) return false;

  if (typeof value === 'string') {
    const stripped = value.replace('%', '').trim();
    return stripped !== '' && Number.isFinite(Number(stripped));
  }

  return typeof value === 'number' && Number.isFinite(value);
};

export const displayPercentage = (value?: number | string | null, precision = 2) => (
  isPercentage(value) ? `${capDecimals(Number(value) * 100, precision)}%` : ''
);

export const parsePercentageToDecimal = (value: string) => {
  if (!isPercentage(value)) return undefined;
  const stripped = value.replace('%', '').trim();
  const [integer, decimals] = stripped.split('.');
  const truncated = decimals ? `${integer}.${decimals.slice(0, 2)}` : integer;
  return Number(truncated) / 100;
};
